import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  Output,
} from '@angular/core';
import {
  CurrentPracticeScope,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import {
  TrackByFunctions,
  TypedFormControl,
  TypedFormGroup,
  validFormGroupChanges$,
} from '@principle-theorem/ng-shared';
import { PracticePermissions } from '@principle-theorem/principle-core/features';
import {
  DEFAULT_HOUR_INCREMENT,
  DEFAULT_PRACTICE_TIMELINE_TOOLTIP_DELAY,
  DEFAULT_STATUS_INDICATOR_CONFIG,
  DEFAULT_STEP_SIZE_IN_MINS,
  EVENT_TYPES,
  EVENT_TYPE_COLOUR_MAP,
  EventType,
  IPractice,
  IPracticeSettings,
  IStatusIndicatorConfig,
  PRACTICE_TIMELINE_TOOLTIP_DELAY_OPTIONS,
  PracticeTimelineHourIncrement,
  PracticeTimelineTooltipDelay,
  STATUS_INDICATOR_TYPES,
  StatusIndicatorMode,
  StatusIndicatorType,
} from '@principle-theorem/principle-core/interfaces';
import {
  DEADZONE_DEFAULT_COLOUR,
  DEADZONE_PALETTE,
  Firestore,
  WithRef,
  filterUndefined,
  snapshot,
} from '@principle-theorem/shared';
import { groupBy, isEqual, pick } from 'lodash';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import {
  startWith,
  map,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';

enum StatusIndicatorCategory {
  Appointment = 'appointment',
  Deposit = 'deposit',
  LabJob = 'labJob',
  Booking = 'booking',
}

type EventColourPair = {
  type: EventType;
  colour: string;
};

type StatusIndicatorPair = {
  type: StatusIndicatorType;
  config: IStatusIndicatorConfig;
};

type GroupedStatusIndicators = {
  category: StatusIndicatorCategory;
  categoryLabel: string;
  indicators: StatusIndicatorPair[];
};

@Component({
  selector: 'pr-timeline-practice-settings',
  templateUrl: './timeline-practice-settings.component.html',
  styleUrls: ['./timeline-practice-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TimelinePracticeSettingsComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  private _practice$: Observable<WithRef<IPractice>>;
  practiceViewSettings$: Observable<IPracticeSettings['timeline']>;
  deadzoneColour$: Observable<string>;
  hasDeadzoneColourOverride$: Observable<boolean>;
  stepSizesInMins: number[] = [5, 10, 15, 20];
  hourIncrements: PracticeTimelineHourIncrement[] = [10, 15, 20, 30];
  tooltipDelays: PracticeTimelineTooltipDelay[] =
    PRACTICE_TIMELINE_TOOLTIP_DELAY_OPTIONS;
  practiceSettingsDisabled$: Observable<boolean>;
  eventColourPairs$: Observable<EventColourPair[]>;
  statusIndicatorPairs$: Observable<StatusIndicatorPair[]>;
  groupedStatusIndicators$: Observable<GroupedStatusIndicators[]>;
  statusIndicatorTypes = STATUS_INDICATOR_TYPES;
  eventTypes = EVENT_TYPES.filter((eventType) => {
    const excludedTypes = [
      EventType.RosteredOn,
      EventType.Appointment,
      EventType.PracticeClosed,
    ];
    return !excludedTypes.includes(eventType);
  });
  deadzonePalette = DEADZONE_PALETTE;
  trackByColourPair = TrackByFunctions.field<EventColourPair>('type');
  trackByStatusIndicator = TrackByFunctions.field<StatusIndicatorPair>('type');
  trackByCategory = TrackByFunctions.field<GroupedStatusIndicators>('category');

  practiceViewSettingsForm = new TypedFormGroup<
    Required<IPracticeSettings>['timeline']
  >({
    stepSizeInMins: new TypedFormControl(DEFAULT_STEP_SIZE_IN_MINS),
    pixelsPerMinute: new TypedFormControl(),
    hourIncrement: new TypedFormControl<PracticeTimelineHourIncrement>(
      DEFAULT_HOUR_INCREMENT
    ),
    tooltipDelay: new TypedFormControl<PracticeTimelineTooltipDelay>(
      DEFAULT_PRACTICE_TIMELINE_TOOLTIP_DELAY
    ),
  });

  @Output() refreshTimeline = new EventEmitter<void>();

  constructor(
    private _organisation: OrganisationService,
    private _practiceScope: CurrentPracticeScope
  ) {
    this.practiceViewSettings$ = validFormGroupChanges$(
      this.practiceViewSettingsForm
    ).pipe(
      startWith(this.practiceViewSettingsForm.value),
      map((settings) => settings ?? {})
    );

    this._practice$ = this._practiceScope.doc$.pipe(filterUndefined());

    this._practice$
      .pipe(take(1), takeUntil(this._onDestroy$))
      .subscribe((practice) =>
        this.practiceViewSettingsForm.patchValue(
          practice.settings.timeline ?? {},
          {
            emitEvent: false,
          }
        )
      );

    this.deadzoneColour$ = this._practice$.pipe(
      map(
        (practice) =>
          practice.settings.deadzoneColourOverride ?? DEADZONE_DEFAULT_COLOUR
      )
    );

    this.hasDeadzoneColourOverride$ = this.deadzoneColour$.pipe(
      map((colour) => colour !== DEADZONE_DEFAULT_COLOUR)
    );

    this.eventColourPairs$ = combineLatest([
      of(this.eventTypes),
      this._practice$,
    ]).pipe(
      map(([eventTypes, practice]) =>
        eventTypes.map((type) => {
          const colour =
            practice.settings.eventColourOverrides?.[type] ??
            EVENT_TYPE_COLOUR_MAP[type];

          return {
            type,
            colour,
          };
        })
      )
    );

    this.statusIndicatorPairs$ = combineLatest([
      of(this.statusIndicatorTypes),
      this._practice$,
    ]).pipe(
      map(([types, practice]) =>
        types.map((type) => {
          const defaultConfig = DEFAULT_STATUS_INDICATOR_CONFIG[type];
          const override = practice.settings.statusIndicatorOverrides?.[type];

          return {
            type,
            config: {
              ...defaultConfig,
              ...override,
            },
          };
        })
      )
    );

    this.groupedStatusIndicators$ = this.statusIndicatorPairs$.pipe(
      map((pairs) => {
        const categoryLabels: Record<StatusIndicatorCategory, string> = {
          [StatusIndicatorCategory.Appointment]: 'Appointment Statuses',
          [StatusIndicatorCategory.Deposit]: 'Deposit Statuses',
          [StatusIndicatorCategory.LabJob]: 'Lab Job Statuses',
          [StatusIndicatorCategory.Booking]: 'Booking Indicators',
        };

        const grouped = groupBy(pairs, (pair) =>
          this._getCategoryFromStatusIndicatorType(pair.type)
        );

        return Object.entries(grouped).map(([category, indicators]) => ({
          category: category as StatusIndicatorCategory,
          categoryLabel: categoryLabels[category as StatusIndicatorCategory],
          indicators,
        }));
      })
    );

    this.practiceViewSettings$
      .pipe(withLatestFrom(this._practice$), takeUntil(this._onDestroy$))
      .subscribe(([settings, practice]) => {
        void Firestore.patchDoc(practice.ref, {
          settings: {
            ...practice.settings,
            timeline: settings,
          },
        });
      });

    this.practiceSettingsDisabled$ = this._organisation.userPermissions$.pipe(
      map(
        (userPermissions) =>
          !userPermissions.includes(PracticePermissions.PracticeConfigure)
      )
    );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  async saveDeadzoneColour(colour?: string): Promise<void> {
    const practice = await snapshot(this._practice$);
    await Firestore.patchDoc(practice.ref, {
      settings: {
        ...practice.settings,
        deadzoneColourOverride: colour ?? DEADZONE_DEFAULT_COLOUR,
      },
    });
    this.refreshTimeline.emit();
  }

  async saveEventColour(eventType: EventType, colour?: string): Promise<void> {
    const practice = await snapshot(this._practice$);
    await Firestore.patchDoc(practice.ref, {
      settings: {
        ...practice.settings,
        eventColourOverrides: {
          ...practice.settings.eventColourOverrides,
          [eventType]: colour ?? EVENT_TYPE_COLOUR_MAP[eventType],
        },
      },
    });
    this.refreshTimeline.emit();
  }

  hasColourOverride(eventColour: EventColourPair): boolean {
    return eventColour.colour !== EVENT_TYPE_COLOUR_MAP[eventColour.type];
  }

  async saveStatusIndicator(
    type: StatusIndicatorType,
    config?: IStatusIndicatorConfig
  ): Promise<void> {
    const practice = await snapshot(this._practice$);

    await Firestore.patchDoc(practice.ref, {
      settings: {
        ...practice.settings,
        statusIndicatorOverrides: {
          ...practice.settings.statusIndicatorOverrides,
          [type]: config ?? DEFAULT_STATUS_INDICATOR_CONFIG[type],
        },
      },
    });

    this.refreshTimeline.emit();
  }

  async saveStatusIndicatorConfig(
    type: StatusIndicatorType,
    currentConfig: IStatusIndicatorConfig,
    configChange: Omit<IStatusIndicatorConfig, 'enabled'>
  ): Promise<void> {
    await this.saveStatusIndicator(type, {
      ...configChange,
      enabled: currentConfig.enabled,
    });
  }

  async saveStatusIndicatorEnabled(
    type: StatusIndicatorType,
    currentConfig: IStatusIndicatorConfig,
    enabled: boolean
  ): Promise<void> {
    await this.saveStatusIndicator(type, {
      ...currentConfig,
      enabled,
    });
  }

  hasStatusIndicatorOverride(pair: StatusIndicatorPair): boolean {
    const compareKeys = [
      'mode',
      'backgroundColour',
      'colour',
      pair.config.mode === StatusIndicatorMode.Character ? 'character' : 'icon',
    ];

    return !isEqual(
      pick(DEFAULT_STATUS_INDICATOR_CONFIG[pair.type], compareKeys),
      pick(pair.config, compareKeys)
    );
  }

  private _getCategoryFromStatusIndicatorType(
    type: StatusIndicatorType
  ): StatusIndicatorCategory {
    const appointmentTypes = [
      StatusIndicatorType.AppointmentNotConfirmed,
      StatusIndicatorType.AppointmentConfirmed,
      StatusIndicatorType.AppointmentHasArrived,
      StatusIndicatorType.AppointmentInProgress,
      StatusIndicatorType.AppointmentCheckingOut,
      StatusIndicatorType.AppointmentComplete,
      StatusIndicatorType.AppointmentCancelled,
    ];

    const labJobTypes = [
      StatusIndicatorType.LabJobOverdue,
      StatusIndicatorType.LabJobSentOrSending,
      StatusIndicatorType.LabJobReceived,
    ];

    const depositTypes = [
      StatusIndicatorType.DepositPaid,
      StatusIndicatorType.DepositAuthorised,
      StatusIndicatorType.DepositRequired,
      StatusIndicatorType.DepositPending,
      StatusIndicatorType.DepositForfeited,
    ];

    if (depositTypes.includes(type)) {
      return StatusIndicatorCategory.Deposit;
    }

    if (appointmentTypes.includes(type)) {
      return StatusIndicatorCategory.Appointment;
    }

    if (labJobTypes.includes(type)) {
      return StatusIndicatorCategory.LabJob;
    }

    return StatusIndicatorCategory.Booking;
  }
}
